/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { FaCheckCircle, FaTimesCircle, FaClock, FaCalendarAlt, FaUser, FaBook, FaInfoCircle, FaCheck, FaExclamationTriangle } from 'react-icons/fa';
import * as adminService from '../services/adminService';

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed top-4 right-4 z-[200] px-6 py-3 rounded-lg shadow-lg ${type === 'success' ? 'bg-green-500' : 'bg-red-500'
      } text-white font-semibold flex items-center gap-2`}>
      {type === 'success' ? <FaCheckCircle /> : <FaExclamationTriangle />}
      {message}
    </div>
  );
};

const AdminPendingTitles = () => {
  const [pendingTitles, setPendingTitles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [selectedTitle, setSelectedTitle] = useState(null);
  const [globalPrice, setGlobalPrice] = useState(0);
  const [approvalData, setApprovalData] = useState({
    submission_deadline: '',
    result_date: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    loadPendingTitles();
    loadGlobalPrice();
  }, []);

  const loadGlobalPrice = async () => {
    try {
      const response = await adminService.getYIArticlePaymentSettings();
      if (response.success) {
        setGlobalPrice(Number(response.data.settings.yi_article_price) || 0);
      }
    } catch (error) {
      console.error('Failed to load global price setting', error);
    }
  };

  const loadPendingTitles = async () => {
    setLoading(true);
    try {
      const response = await adminService.getPendingRunningTitles();
      if (response.success) {
        setPendingTitles(response.data);
      }
    } catch (error) {
      showToast('Failed to load pending titles', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const handleApproveClick = (title) => {
    setSelectedTitle(title);
    setApprovalData({
      submission_deadline: '',
      result_date: ''
    });
    setShowApproveModal(true);
  };

  const handleApproveSubmit = async () => {
    if (!approvalData.submission_deadline || !approvalData.result_date) {
      return showToast('Please set both deadline and result dates', 'error');
    }

    const deadline = new Date(approvalData.submission_deadline);
    const result = new Date(approvalData.result_date);
    if (result <= deadline) {
      return showToast('Result date must be after submission deadline', 'error');
    }

    setIsProcessing(true);
    try {
      await adminService.approveRunningTitle(selectedTitle.id, approvalData);
      showToast('Title approved and published successfully!', 'success');
      setShowApproveModal(false);
      loadPendingTitles();
    } catch (error) {
      showToast(error.message || 'Failed to approve title', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12 px-4 sm:px-6 lg:px-8">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-3xl shadow-xl p-8 mb-8 border border-gray-100 border-l-8 border-l-yellow-400">
           <div className="flex items-center gap-6">
              <div className="w-16 h-16 bg-yellow-100 rounded-2xl flex items-center justify-center">
                 <FaClock className="text-yellow-600 text-3xl" />
              </div>
              <div>
                 <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Pending Approvals</h1>
                 <p className="text-gray-500 mt-1">Review and publish article titles submitted by Editorial Board members</p>
              </div>
           </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
             <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100 mb-10">
            {pendingTitles.length > 0 ? (
              <>
                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-100">
                        <th className="px-6 py-5 text-xs font-bold text-gray-400 uppercase tracking-wider">Title & Author</th>
                        <th className="px-6 py-5 text-xs font-bold text-gray-400 uppercase tracking-wider">Department</th>
                        <th className="px-6 py-5 text-xs font-bold text-gray-400 uppercase tracking-wider">Languages</th>
                        <th className="px-6 py-5 text-xs font-bold text-gray-400 uppercase tracking-wider text-right">Global Price</th>
                        <th className="px-6 py-5 text-xs font-bold text-gray-400 uppercase tracking-wider text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {pendingTitles.map((rt) => (
                        <tr key={rt.id} className="hover:bg-gray-50/50 transition-colors group">
                          <td className="px-6 py-6">
                            <p className="font-bold text-gray-900 group-hover:text-indigo-600 transition-colors uppercase tracking-tight">{rt.title}</p>
                            <p className="text-xs text-gray-400 mt-1.5 flex items-center gap-2 font-mono">
                               <span className="flex items-center gap-1 bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full font-bold text-[10px]">
                                  <FaUser /> {rt.creator?.full_name || 'EB Member'}
                               </span>
                               <span className="bg-gray-100 px-2 py-0.5 rounded-full text-[10px]">ID: {rt.id.slice(0, 8)}</span>
                            </p>
                          </td>
                          <td className="px-6 py-6 text-sm">
                             <div className="space-y-1">
                                <p className="font-bold text-gray-700 flex items-center gap-2 uppercase tracking-tight text-xs">
                                   <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                                   {rt.subject?.name}
                                </p>
                                 <p className="text-gray-400 text-[10px] pl-3.5 italic uppercase">{rt.stream?.name}</p>
                                 {rt.specialization?.name && (
                                   <p className="text-indigo-400 text-[9px] pl-3.5 italic uppercase font-medium">{rt.specialization.name}</p>
                                 )}
                             </div>
                          </td>
                          <td className="px-6 py-6 text-sm">
                             <div className="flex flex-wrap gap-1">
                               {rt.languages_allowed.map((l, i) => (
                                 <span key={i} className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-[10px] font-black uppercase border border-indigo-100">{l}</span>
                               ))}
                             </div>
                          </td>
                          <td className="px-6 py-6 text-right">
                             <div className="flex flex-col">
                                <span className="font-black text-indigo-600 text-lg">₹{globalPrice}</span>
                                <span className="text-[10px] text-gray-400 uppercase font-bold tracking-tighter">Fixed Setting</span>
                             </div>
                          </td>
                          <td className="px-6 py-6 text-right">
                             <button 
                              onClick={() => handleApproveClick(rt)}
                              className="bg-indigo-600 text-white px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-indigo-700 hover:scale-105 active:scale-95 transition-all shadow-lg hover:shadow-indigo-200 flex items-center gap-3 ml-auto group/btn"
                             >
                                <FaCheck className="group-hover/btn:scale-125 transition-transform" /> Review
                             </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden divide-y divide-gray-100">
                   {pendingTitles.map((rt) => (
                     <div key={rt.id} className="p-6 space-y-5 active:bg-gray-50 transition-colors">
                        <div className="flex justify-between items-start gap-4">
                           <div>
                              <h3 className="font-bold text-gray-900 leading-tight uppercase tracking-tight">{rt.title}</h3>
                              <p className="text-[10px] text-gray-400 mt-1 flex items-center gap-1">
                                 <FaUser className="text-[8px]" /> {rt.creator?.full_name || 'EB Member'}
                              </p>
                           </div>
                           <div className="text-right">
                              <span className="font-black text-indigo-600 text-base">₹{globalPrice}</span>
                           </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 bg-gray-50 rounded-2xl p-4">
                           <div className="space-y-1">
                              <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Department</p>
                              <p className="text-xs font-bold text-gray-700 truncate">{rt.subject?.name}</p>
                           </div>
                           <div className="space-y-1">
                              <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Stream</p>
                              <p className="text-xs text-gray-600 truncate">{rt.stream?.name || 'N/A'}</p>
                           </div>
                        </div>

                        <div className="flex flex-wrap gap-1">
                           {rt.languages_allowed.map((l, i) => (
                             <span key={i} className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-[9px] font-black uppercase border border-indigo-100">{l}</span>
                           ))}
                        </div>

                        <button 
                          onClick={() => handleApproveClick(rt)}
                          className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg shadow-indigo-100 active:scale-95 transition-all flex items-center justify-center gap-2"
                        >
                           <FaCheck /> Review & Approve
                        </button>
                     </div>
                   ))}
                </div>
              </>
            ) : (
              <div className="py-32 px-6 text-center bg-white">
                 <div className="max-w-md mx-auto">
                    <div className="relative mb-8 flex justify-center">
                       <div className="absolute inset-0 bg-indigo-100 rounded-full scale-150 blur-3xl opacity-50"></div>
                       <div className="w-24 h-24 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl flex items-center justify-center shadow-2xl transform rotate-12 relative z-10">
                          <FaCheckCircle className="text-white text-5xl" />
                       </div>
                    </div>
                    <h2 className="text-3xl font-black text-gray-900 mb-4 tracking-tight">Zero Pending Tasks!</h2>
                    <p className="text-gray-500 font-medium mb-8 leading-relaxed">
                       You've cleared all the running titles submitted by the Editorial Board. There's nothing new to approve at the moment.
                    </p>
                    <div className="flex justify-center gap-4">
                       <span className="px-4 py-2 bg-green-50 text-green-700 rounded-full text-xs font-bold border border-green-100 flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                          Status: Up to date
                       </span>
                    </div>
                 </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Approval Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[110] p-4 animate-fade-in text-left">
            <div className="bg-white rounded-[2.5rem] shadow-2xl max-w-lg w-full overflow-hidden animate-modal-slide-up shadow-indigo-500/10 border border-white/20">
              <div className="bg-gradient-to-r from-indigo-600 via-indigo-700 to-purple-700 p-6 md:p-10 text-white relative overflow-hidden">
                 {/* Decorative Background Pattern */}
                 <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 blur-2xl"></div>
                 <div className="absolute bottom-0 left-0 w-24 h-24 bg-purple-500/20 rounded-full -ml-12 -mb-12 blur-xl"></div>
                 
                 <div className="relative z-10 flex justify-between items-center">
                    <div>
                       <h2 className="text-2xl md:text-3xl font-black tracking-tight leading-tight">Finalize Approval</h2>
                       <p className="text-indigo-100 text-xs md:text-sm mt-1 font-medium opacity-90">Set deadlines before publishing to students</p>
                    </div>
                    <button 
                      onClick={() => setShowApproveModal(false)}
                      className="bg-white/10 hover:bg-white/20 p-2.5 md:p-3 rounded-xl md:rounded-2xl transition-all backdrop-blur-md border border-white/10 shadow-xl"
                    >
                       <FaTimesCircle className="text-lg md:text-xl" />
                    </button>
                 </div>
              </div>

              <div className="p-6 md:p-10 space-y-8">
                 <div className="space-y-6">
                    <div className="group">
                        <label className="block text-[10px] font-black text-indigo-400 uppercase tracking-[0.2em] mb-3">Submission Deadline *</label>
                        <div className="relative">
                           <FaCalendarAlt className="absolute left-5 top-1/2 -translate-y-1/2 text-indigo-400 text-lg group-focus-within:text-indigo-600 transition-colors" />
                           <input 
                            type="date"
                            value={approvalData.submission_deadline}
                            onChange={e => setApprovalData({...approvalData, submission_deadline: e.target.value})}
                            className="w-full pl-14 pr-6 py-4 bg-gray-50 border-2 border-transparent focus:border-indigo-600 focus:bg-white rounded-2xl transition-all font-bold text-gray-700 shadow-inner"
                           />
                        </div>
                    </div>

                    <div className="group">
                        <label className="block text-[10px] font-black text-indigo-400 uppercase tracking-[0.2em] mb-3">Result Declaration *</label>
                        <div className="relative">
                           <FaCalendarAlt className="absolute left-5 top-1/2 -translate-y-1/2 text-indigo-400 text-lg group-focus-within:text-indigo-600 transition-colors" />
                           <input 
                            type="date"
                            value={approvalData.result_date}
                            onChange={e => setApprovalData({...approvalData, result_date: e.target.value})}
                            className="w-full pl-14 pr-6 py-4 bg-gray-50 border-2 border-transparent focus:border-indigo-600 focus:bg-white rounded-2xl transition-all font-bold text-gray-700 shadow-inner"
                           />
                        </div>
                    </div>
                 </div>

                 <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-[2rem] p-5 md:p-7 border-2 border-amber-100 shadow-xl shadow-amber-500/5 items-center flex justify-between">
                    <div className="space-y-1">
                       <label className="block text-[10px] font-black text-amber-600 uppercase tracking-wider">Publishing Price</label>
                       <p className="text-gray-500 text-[10px] font-medium leading-tight">Applied from <br className="hidden md:block" />global settings</p>
                    </div>
                    <div className="text-right">
                       <span className="text-2xl md:text-4xl font-black text-gray-900 tracking-tighter">₹{globalPrice}</span>
                    </div>
                 </div>
              </div>

              <div className="p-6 md:p-10 bg-gray-50 border-t-2 border-gray-100 flex flex-col sm:flex-row gap-4">
                 <button 
                  onClick={() => setShowApproveModal(false)}
                  className="order-2 sm:order-1 flex-1 py-4 bg-white border-2 border-indigo-100 text-indigo-500 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-indigo-50 hover:text-indigo-600 transition-all active:scale-95 shadow-sm"
                 >
                    Cancel
                 </button>
                 <button 
                  onClick={handleApproveSubmit}
                  disabled={isProcessing}
                  className="order-1 sm:order-2 flex-[2] py-4 bg-indigo-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-indigo-200 hover:bg-indigo-700 hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-70 flex items-center justify-center gap-3"
                 >
                    {isProcessing ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        <FaCheck className="text-lg" />
                        Approve & Publish
                      </>
                    )}
                 </button>
              </div>
           </div>
        </div>
      )}
    </div>
  );
};

export default AdminPendingTitles;
